import { useSelectUserContext } from '../../selectors/userContext.selectors';
import { getS3DataAuthCookies } from '../../services/dataAuthorizer';
import { UserContext } from '../../types/userContext';
import React, {
	ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useState,
	useRef,
} from 'react';
import S3DataAuthContext, {
	S3DataAuthState,
	AuthorizeS3DataCallsFunc,
	S3DataAuthContextValue,
} from './S3DataAuthContext';
import axios, { AxiosError } from 'axios';

export interface S3DataAuthProviderProps {
	isLandingPage?: boolean;
	children: ReactNode;
}

const S3DataAuthProvider = ({
	isLandingPage = false,
	children,
}: S3DataAuthProviderProps): JSX.Element => {
	const userContext = useSelectUserContext();
	const [status, setState] = useState<S3DataAuthState>('idle');

	const abortControllerRef = useRef<AbortController>();

	const authorizeS3DataCalls = useCallback<AuthorizeS3DataCallsFunc>(() => {
		if (isLandingPage) return;
		abortControllerRef.current?.abort();
		abortControllerRef.current = new AbortController();
		setState('loading');
		getS3DataAuthCookies(abortControllerRef.current.signal)
			.then(() => setState('resolved'))
			.catch((error: AxiosError) => {
				if (axios.isCancel(error)) {
					return;
				}
				setState('rejected');
			});
	}, []);

	const isUserAuthenticatedInApp = useMemo<boolean>(
		() => checkIfUserIsAuthenticatedInApp(userContext),
		[userContext],
	);

	const contextValue = useMemo<S3DataAuthContextValue>(
		() => ({
			status,
			authorizeS3DataCalls,
		}),
		[status, authorizeS3DataCalls],
	);

	useEffect(() => {
		return () => abortControllerRef.current?.abort();
	}, []);

	useEffect(() => {
		if (isUserAuthenticatedInApp) {
			authorizeS3DataCalls();
		}
	}, [isUserAuthenticatedInApp]);

	return !isLandingPage ? (
		<S3DataAuthContext.Provider value={contextValue}>
			{children}
		</S3DataAuthContext.Provider>
	) : (
		<>{children}</>
	);
};

function checkIfUserIsAuthenticatedInApp(userContext: UserContext): boolean {
	return !!(
		userContext &&
		userContext.defaultSite &&
		userContext.innovyzeRole
	);
}

export default S3DataAuthProvider;
